import { createFileRoute } from '@tanstack/react-router'
import { Fragment } from 'react'
import { trpc } from '@/lib/trpc'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { ArrowLeft } from 'lucide-react'

export const Route = createFileRoute('/(protected)/workspaces/$workspaceId/steps/$id/')({
  component: StepDetail,
})

function StepDetail() {
  const { id } = Route.useParams()
  const { data, isLoading, error } = trpc.steps.getStepById.useQuery({ id })
  
  if (isLoading) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex items-center gap-2 mb-6">
          <Button variant="ghost" size="sm">
            <span className="mr-2">←</span> Back
          </Button>
          <Skeleton className="h-8 w-1/3" />
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-1/4 mb-2" />
            <Skeleton className="h-4 w-1/3" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    )
  }
  
  if (error || !data?.step) {
    return (
      <div className="container mx-auto py-8">
        <Card>
          <CardHeader>
            <CardTitle>Error</CardTitle>
            <CardDescription>
              {error?.message || 'Step not found'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild>
              <a href="/steps">Back to Steps</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }
  
  const { step } = data
  
  // Format timestamp to readable date
  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleString()
  }
  
  // Format duration in milliseconds to readable format
  const formatDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000)
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }
  
  return (
    <div className="container mx-auto py-8">
      <div className="flex items-center gap-2 mb-6">
        <Button variant="ghost" size="sm" asChild>
          <a href="/steps">
            <span className="mr-2">←</span> Back to Steps
          </a>
        </Button>
      </div>
      
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-2xl">
              {step.action.charAt(0).toUpperCase() + step.action.slice(1)} Action
            </CardTitle>
            <span className="text-sm text-muted-foreground">
              ID: {step.id}
            </span>
          </div>
          <CardDescription>
            Recorded at {formatTimestamp(step.timestamp)} ({formatDuration(step.relativeTime)} from start)
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-2">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium">Step Type</p>
                <p className="text-sm">{step.stepType}</p>
              </div>
              <div>
                <p className="text-sm font-medium">Action</p>
                <p className="text-sm">{step.action}</p>
              </div>
              <div>
                <p className="text-sm font-medium">URL</p>
                <p className="text-sm truncate">{step.url}</p>
              </div>
              {step.previousUrl && (
                <div>
                  <p className="text-sm font-medium">Previous URL</p>
                  <p className="text-sm truncate">{step.previousUrl}</p>
                </div>
              )}
            </div>
          </div>
          
          <Separator />
          
          <div>
            <h3 className="text-lg font-semibold mb-2">Element Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {step.elementType && (
                <div>
                  <p className="text-sm font-medium">Element Type</p>
                  <p className="text-sm">{step.elementType}</p>
                </div>
              )}
              {step.target && (
                <div>
                  <p className="text-sm font-medium">Target</p>
                  <p className="text-sm">{step.target}</p>
                </div>
              )}
              {step.domText && (
                <div>
                  <p className="text-sm font-medium">DOM Text</p>
                  <p className="text-sm">{step.domText}</p>
                </div>
              )}
              {step.tooltip && (
                <div>
                  <p className="text-sm font-medium">Tooltip</p>
                  <p className="text-sm">{step.tooltip}</p>
                </div>
              )}
              {step.value && (
                <div>
                  <p className="text-sm font-medium">Value</p>
                  <p className="text-sm">{step.value}</p>
                </div>
              )}
              {step.initialValue && (
                <div>
                  <p className="text-sm font-medium">Initial Value</p>
                  <p className="text-sm">{step.initialValue}</p>
                </div>
              )}
            </div>
          </div>
          
          {step.screenshot && (
            <Fragment>
              <Separator />
              <div>
                <h3 className="text-lg font-semibold mb-2">Screenshot</h3>
                <div className="border rounded-md overflow-hidden">
                  <img 
                    src={step.screenshot} 
                    alt={`Screenshot of ${step.action} action`}
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </Fragment>
          )}
          
          {step.topics && step.topics.length > 0 && (
            <Fragment>
              <Separator />
              <div>
                <h3 className="text-lg font-semibold mb-2">Topics</h3>
                <div className="flex flex-wrap gap-2">
                  {step.topics.map((topic) => (
                    <div key={topic.id} className="bg-muted px-3 py-1 rounded-full text-sm">
                      {topic.title}
                    </div>
                  ))}
                </div>
              </div>
            </Fragment>
          )}
        </CardContent>
      </Card>
    </div>
  )
} 